import * as THREE from 'three';
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js';

/**
 * HdriEnvironment.ts — loads an equirectangular sky and turns it into BOTH a
 * visible sky background AND a prefiltered IBL reflection environment, so the
 * cars, glass, ball and wet road reflect a real sky and the whole scene picks up
 * its colour. A city opts in via Theme.env; anything without one keeps the
 * procedural gradient dome (EnvironmentBuilder).
 *
 * MOBILE (sky-compression pass): cities ship a tone-mapped LDR .jpg sky
 * (~50–200 KB each, vs multi-MB .hdr). The visible sky stays crisp, and an LDR
 * IBL is plenty for the stylized look — the scene's directional sun still casts
 * the hard key light + specular highlight, so reflections lose nothing the eye
 * notices. The loader auto-detects by extension, so a true .hdr can still be
 * dropped in for a city that wants high-dynamic-range lighting. Skies are CC0
 * (Poly Haven).
 *
 * Each sky is parsed ONCE and cached for the session (the title flythrough reloads
 * cities constantly), and the cached textures are intentionally NOT freed by the
 * per-city mesh disposer.
 */
export interface HdriResult {
  /** Prefiltered radiance map for scene.environment (reflections + ambient). */
  environment: THREE.Texture;
  /** The equirect sky itself, for scene.background (the visible sky). */
  background: THREE.Texture;
}

export class HdriEnvironment {
  private readonly hdrLoader = new HDRLoader();
  private readonly texLoader = new THREE.TextureLoader();
  private readonly pmrem: THREE.PMREMGenerator;
  private readonly cache = new Map<string, Promise<HdriResult>>();

  constructor(renderer: THREE.WebGLRenderer) {
    this.pmrem = new THREE.PMREMGenerator(renderer);
    this.pmrem.compileEquirectangularShader();
  }

  /** Load (or return cached) equirect sky → { environment, background }. */
  load(url: string): Promise<HdriResult> {
    let p = this.cache.get(url);
    if (!p) {
      const isHdr = /\.(hdr|exr)$/i.test(url);
      const loaded = isHdr ? this.hdrLoader.loadAsync(url) : this.texLoader.loadAsync(url);
      p = loaded.then((tex) => {
        tex.mapping = THREE.EquirectangularReflectionMapping;
        // HDR data is linear; a tone-mapped .jpg sky is sRGB-encoded.
        tex.colorSpace = isHdr ? THREE.NoColorSpace : THREE.SRGBColorSpace;
        const environment = this.pmrem.fromEquirectangular(tex).texture;
        return { environment, background: tex };
      });
      this.cache.set(url, p);
    }
    return p;
  }

  dispose(): void {
    this.pmrem.dispose();
    for (const p of this.cache.values()) {
      p.then(({ environment, background }) => {
        environment.dispose();
        background.dispose();
      }).catch(() => {});
    }
    this.cache.clear();
  }
}
